import * as React from 'react';
import {
  ClipboardList,
  Calendar,
  Clock,
  Users,
  Car,
  MapPin,
  Percent,
  FileText,
  Scale,
  TriangleAlert,
  Building2,
  Tag,
  User,
} from 'lucide-react';
import { Card } from '@components/base/layout';
import { Text, Icon, InfoList } from '@components/base/display';
import type { InfoItemDescriptor } from '@components/base/display';
import {
  buildPublishedConfigFromInfoItems,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import type { ClaimSummary } from '../types';
import { formatDateFr } from '../configs/staticData';

interface ClaimSummaryCardProps {
  claim: ClaimSummary;
  /**
   * Tenant-scoped layout overrides, injected by the `withComponentRules` HOC
   * (do not pass by hand). Threaded straight into the grid {@link InfoList};
   * with no rules authored this is `undefined` and the render is identical to
   * the pre-migration card. Only the `items` bucket is consumed, keyed by the
   * stable descriptor keys built in {@link buildClaimSummaryFields}.
   */
  _layoutOverrides?: LayoutOverrides;
}

/**
 * The stable contract keys a tenant rule targets for the configurable grid
 * fields. Each key is a `ClaimSummary` view-model property (camelCase) — the
 * permanent contract — kept separate from the display `label`, which is
 * overridable. The header bar (`client` / `dossierNumber`) and the
 * description block stay bespoke chrome and are not part of this set.
 */
export type ClaimSummaryFieldKey =
  | 'date'
  | 'openingDate'
  | 'status'
  | 'materialManager'
  | 'bodilyManager'
  | 'plate'
  | 'location'
  | 'responsibility'
  | 'type'
  | 'article'
  | 'collisionType'
  | 'company'
  | 'damageNature'
  | 'thirdParty';

/**
 * Build the ordered, declarative descriptor array for the info grid. Pure
 * function of `claim`: each entry carries a stable `key` (the contract a rule
 * targets), the current French `label` (display-only, overridable), the
 * pre-formatted `value`, the per-field `icon`, and the `truncate` flag for
 * long values. The two manager rows additionally set `block: true` so the
 * inline `InfoList` renders them as the original two-line stacked cells (label
 * above value). `order` is the array index so the default render preserves the
 * pre-migration source order; rules may override it. Dates go through
 * `formatDateFr`; `responsibility` is rendered verbatim (already `"NN%"` from
 * the mapper). No null/empty handling here — the mapper coalesces to `'-'`.
 */
function buildClaimSummaryFields(
  claim: ClaimSummary,
): InfoItemDescriptor[] {
  return [
    { key: 'date', label: 'Date sinistre:', value: formatDateFr(claim.date), icon: Calendar },
    { key: 'openingDate', label: 'Date ouverture:', value: formatDateFr(claim.openingDate), icon: Calendar },
    { key: 'status', label: 'Statut:', value: claim.status, icon: Clock },
    { key: 'materialManager', label: 'Gestionnaire mat:', value: claim.materialManager, icon: Users, truncate: true, block: true },
    { key: 'bodilyManager', label: 'Gestionnaire corp:', value: claim.bodilyManager, icon: Users, truncate: true, block: true },
    { key: 'plate', value: claim.plate, icon: Car },
    { key: 'location', value: claim.location, icon: MapPin, truncate: true },
    { key: 'responsibility', label: 'Resp:', value: claim.responsibility, icon: Percent },
    { key: 'type', value: claim.type, icon: FileText },
    { key: 'article', value: claim.article, icon: Scale, truncate: true },
    { key: 'collisionType', value: claim.collisionType, icon: TriangleAlert, truncate: true },
    { key: 'company', value: claim.company, icon: Building2, truncate: true },
    { key: 'damageNature', value: claim.damageNature, icon: Tag },
    { key: 'thirdParty', label: 'Tiers:', value: claim.thirdParty, icon: User },
  ];
}

export function ClaimSummaryCard({
  claim,
  _layoutOverrides,
}: Readonly<ClaimSummaryCardProps>) {
  const fields = React.useMemo(
    () => buildClaimSummaryFields(claim),
    [claim],
  );

  usePublishLayoutConfig(
    'claimflow.claimSummaryCard',
    buildPublishedConfigFromInfoItems(fields, 'Résumé du sinistre'),
  );

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <Icon icon={ClipboardList} size="md" color="primary" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle('Résumé du sinistre', _layoutOverrides)}
          </Text>
        </div>
      </Card.Header>
      <Card.Content>
        {/* Client + Dossier bar */}
        <div className="flex items-center gap-4 mb-5 pb-4 border-b border-secondary-100">
          <Text as="span" size="sm" color="muted">
            Client:{' '}
            <Text as="span" weight="semibold" color="default">
              {claim.client}
            </Text>
          </Text>
          <Text as="span" size="sm" color="muted">
            Dossier:{' '}
            <span className="inline-flex items-center rounded bg-secondary-100 px-2 py-0.5 font-mono text-xs font-medium text-secondary-700">
              {claim.dossierNumber}
            </span>
          </Text>
        </div>

        {/* Info grid — declarative, rule-targetable via _layoutOverrides.items.
            `variant="inline"` + `columns="auto"` + the raw-Lucide
            `iconClassName` restore the pre-migration look pixel-for-pixel: each
            pair is a single `icon + "label value"` row (the two manager rows
            opt into a two-line stacked cell via `block: true`), flowed into the
            responsive auto-fill grid, with the original `strokeWidth={2}`
            primary-400 icons. */}
        <InfoList
          className="mb-5"
          columns="auto"
          variant="inline"
          iconClassName="w-4 h-4 text-primary-400 shrink-0"
          items={fields}
          layoutOverrides={_layoutOverrides}
        />

        {/* Description */}
        <div className="bg-secondary-50 rounded-lg p-4">
          <Text size="sm" color="muted">
            {claim.description}
          </Text>
        </div>
      </Card.Content>
    </Card>
  );
}
